import { Component } from '@eppendorf/vnls-device-service-utils';
import { useEffect, useState } from 'react';
import { useForm, FieldPath, FieldErrors } from 'react-hook-form';

import { useUpdateThresholdsSnapshot } from '$features/monitoring/parameters/snapshot/alerting.api.snapshot';
import { mapAlertThresholdsToFormValuesSnapshot } from '$features/monitoring/parameters/snapshot/thresholds-form-snapshot/thresholds-form-mapper-snapshot';
import {
  ThresholdsFormProfile,
  ThresholdsFormDefaultValues,
  ThresholdsFormErrorTypes,
} from '$features/monitoring/parameters/thresholds-form/thresholds-form-profile';

export interface ThresholdsFormOptionsSnapshot {
  shadowComponent?: Component;
  deviceId: string;
}

export const useThresholdsFormSnapshot = ({
  shadowComponent,
  deviceId,
}: ThresholdsFormOptionsSnapshot) => {
  const [isEditing, setIsEditing] = useState(false);

  const { mutateAsync } = useUpdateThresholdsSnapshot(deviceId);

  const handleSubmitThresholds = (thresholds: ThresholdsFormProfile) =>
    mutateAsync({ telemetryId: shadowComponent?.id ?? '', thresholds });

  const form = useForm<ThresholdsFormProfile>({
    defaultValues: ThresholdsFormDefaultValues,
    mode: 'onBlur',
    resolver: (formValues) => {
      const errors: FieldErrors<ThresholdsFormProfile> = {};

      const fieldsToValidate: Exclude<FieldPath<ThresholdsFormProfile>, 'active'>[] = [
        'alertHigh',
        'warnHigh',
        'warnLow',
        'alertLow',
      ];

      for (let i = 0; i < fieldsToValidate.length; i += 1) {
        // eslint-disable-next-line security/detect-object-injection -- this is safe
        const key = fieldsToValidate[i];
        // eslint-disable-next-line security/detect-object-injection -- this is safe
        const currentValue = formValues[key];
        const nextValue = formValues[fieldsToValidate[i + 1]];

        if (!currentValue || Number.isNaN(Number(currentValue))) {
          // eslint-disable-next-line security/detect-object-injection -- this is safe
          errors[key] = { type: 'required' };
        }

        if (currentValue && nextValue && Number(currentValue) <= Number(nextValue)) {
          // eslint-disable-next-line security/detect-object-injection -- this is safe
          errors[key] = { type: ThresholdsFormErrorTypes.limits };
        }
      }

      return Object.keys(errors).length > 0
        ? { values: {} as Record<string, never>, errors }
        : { values: formValues, errors: {} as Record<string, never> };
    },
  });

  const {
    formState,
    setError,
    setValue,
    watch,
    reset,
    handleSubmit: baseHandleSubmit,
  } = form;

  useEffect(() => {
    if (shadowComponent) {
      reset(mapAlertThresholdsToFormValuesSnapshot(shadowComponent));
    }
  }, [reset]);

  const handleAlertThresholdsToggle = (enabled: boolean): void => {
    setValue('active', enabled);
  };

  const alertThresholdsEnabled = watch('active');

  const handleReset = () => {
    reset();

    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = async (formValues: ThresholdsFormProfile) => {
    try {
      await handleSubmitThresholds(formValues);
      setIsEditing(false);
    } catch (error) {
      setError('root', {
        message: 'Error while saving thresholds. Please try again.',
      });
    }
  };

  const { isSubmitting, isValid } = formState;

  const canSubmit = isValid;

  return {
    isEditing,
    canSubmit,
    isSubmitting,
    alertThresholdsEnabled,
    handleAlertThresholdsToggle,
    handleReset,
    handleEdit,
    handleSubmit: baseHandleSubmit(handleSubmit),
    form,
  };
};
